import {
  goal as goalConfig,
  gridPage as gridPageConfig,
  notification as notificationConfig,
  planning as planningConfig,
  space as spaceConfig,
  update as updateConfig,
  user as userConfig,
} from 'shared/api/query/configs.json';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { legacyRoutePrefix } from '@/router';
import { routeName } from 'shared/constants.json';

// DISCOVER_TENANT turns the identity's reachable accounts plus the current route
// into the one tenant the app connects to — without any tenant-scoped read, which
// is resource-filtered and would 403 before a tenant is known. An unreachable URL
// candidate recovers to the identity's preferred account rather than failing.

const findAccountBySlug = (accounts, slug) => accounts.find((account) => {
  if (account.activeSlug?.slug === slug) {
    return true;
  }
  return (account.slugs ?? []).some((s) => s.slug === slug);
});

const findAccountById = (accounts, id) => accounts.find((account) => account.uid === id);

// A freshly-signed-up account has no slug yet (assigned when the company is
// named during onboarding), so activeSlug may be null — resolve it to null
// rather than throwing; the account id still drives X-Tenant-Id.
const tenant = (account, recoveredFrom) => {
  if (!account) {
    return null;
  }
  const resolved = { slug: account.activeSlug?.slug ?? null, accountId: account.uid };
  if (recoveredFrom) {
    resolved.recoveredFrom = recoveredFrom;
  }
  return resolved;
};

// Resource-bearing routes whose id param maps cleanly to a /find-account model.
// dataSourceDetails is excluded on purpose (its param is a connector *type*, not
// a resource uid); savedView lives behind the public/published flow, not here.
// /find-account model strings equal the nebula config.model values, which in
// turn equal the Go xfmr `Prefix()` values checked against dgraph.type.
const ROUTE_RESOURCE = {
  [routeName.goalDetails]: { model: goalConfig.model, param: 'goalId' },
  [routeName.spaceDetails]: { model: spaceConfig.model, param: 'optionId' },
  [routeName.dashboardDetails]: { model: gridPageConfig.model, param: 'pageId' },
  [routeName.updateDetails]: { model: updateConfig.model, param: 'id' },
  [routeName.planningDetails]: { model: planningConfig.model, param: 'id' },
  [routeName.profile]: { model: userConfig.model, param: 'userId' },
  [routeName.updateScheduleDetails]: { model: notificationConfig.model, param: 'notificationId' },
};

const normalizeRouteName = (name) => (typeof name === 'string' && name.startsWith(legacyRoutePrefix)
  ? name.slice(legacyRoutePrefix.length)
  : name);

const resourceFromRoute = (route) => {
  const entry = ROUTE_RESOURCE[normalizeRouteName(route?.name)];
  if (!entry) {
    return null;
  }
  const uid = Number(route?.params?.[entry.param]);
  if (!Number.isFinite(uid) || uid <= 0) {
    return null;
  }
  return { model: entry.model, uid };
};

export const discoverTenant = (route, { findAccount, accounts = [], lastActiveAccount } = {}) => {
  const preferred = lastActiveAccount ?? accounts[0];
  const useOrRecover = (account, rejectedSlug) => (account
    ? tenant(account)
    : tenant(preferred, rejectedSlug));

  const slug = route?.params?.tenantSlug;
  if (slug) {
    return Promise.resolve(useOrRecover(findAccountBySlug(accounts, slug), slug));
  }

  const resource = resourceFromRoute(route);
  if (!resource || typeof findAccount !== 'function') {
    return Promise.resolve(tenant(preferred));
  }

  return findAccount(resource).then((response) => {
    const accountId = response?.data?.accountID;
    if (response?.status !== 200 || isNullOrUndefined(accountId)) {
      return tenant(preferred);
    }
    return useOrRecover(findAccountById(accounts, accountId), response.data.accountSlug);
  });
};
